import { useState, useRef, useEffect } from 'react';
import { Search, Plus, CheckCircle2, Loader2, Tag, Check, X, MapPin, Building2, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { INPUT_CLS } from './constants';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/** Inline Barbour ID editor shown when a project has no Barbour ID. */
function BarbourIdAdder({ project, onSaved }) {
  const [open, setOpen]       = useState(false);
  const [value, setValue]     = useState('');
  const [saving, setSaving]   = useState(false);
  const inputRef              = useRef(null);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const save = async (e) => {
    e.stopPropagation(); // don't trigger project selection
    const trimmed = value.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/zoho/projects/${project.id}/barbour-id`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ barbour_id: trimmed }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to save');
      toast.success(`Barbour ID saved: ${trimmed}`);
      onSaved(project.id, trimmed); // update parent list
      setOpen(false);
      setValue('');
    } catch (err) {
      toast.error(err.message || 'Could not save Barbour ID');
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="flex items-center gap-1 text-[10px] font-semibold text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded-full transition-colors flex-shrink-0"
        data-testid={`add-barbour-btn-${project.id}`}
      >
        <Tag className="w-2.5 h-2.5" /> Add Barbour ID
      </button>
    );
  }

  return (
    <div
      className="flex items-center gap-1 flex-shrink-0"
      onClick={(e) => e.stopPropagation()}
    >
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') save(e); if (e.key === 'Escape') { setOpen(false); setValue(''); } }}
        placeholder="Barbour ID"
        className="w-28 h-7 border border-blue-300 rounded-lg px-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
        data-testid={`barbour-id-input-${project.id}`}
      />
      <button
        type="button"
        onClick={save}
        disabled={saving || !value.trim()}
        className="w-6 h-6 flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white rounded-full flex-shrink-0 disabled:opacity-40"
        data-testid={`barbour-id-confirm-${project.id}`}
      >
        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(false); setValue(''); }}
        className="w-6 h-6 flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-500 rounded-full flex-shrink-0"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

export const StepProject = ({
  step, mode, showAccessRequest,
  projectQuery, setProjectQuery, projectLoading, projects,
  selectedProject, setSelectedProject,
  showProjectCreate, setShowProjectCreate,
  newProjectName, setNewProjectName,
  newProjectBarbourId, setNewProjectBarbourId,
  newProjectPostcode, setNewProjectPostcode,
  creatingProject, runProjectSearch, createProject,
  quoteForm, setStep,
  showBarbourSearch, setShowBarbourSearch,
  barbourPostcode, setBarbourPostcode,
  barbourRadius, setBarbourRadius, barbourMaxRadius,
  barbourSearching, barbourResults, barbourSearchError, searchBarbourForProject,
  crmCheckStatus, checkCrmForBarbourResult, selectCrmMatchFromBarbour, addBarbourResultToCrm,
  onProjectsUpdate,
}) => {
  if (step !== 1 || mode !== 'project' || showAccessRequest) return null;

  /** Update the Barbour ID for a project in the local list without re-fetching. */
  const handleBarbourSaved = (projectId, newBarbourId) => {
    if (onProjectsUpdate) {
      onProjectsUpdate(projectId, { barbour_project_id: newBarbourId });
    }
  };

  return (
    <>
      {!showProjectCreate && (
        <>
          <Label className="text-sm font-semibold">Search project in Zoho</Label>
          <div className="flex gap-2">
            <Input
              value={projectQuery}
              onChange={(e) => setProjectQuery(e.target.value)}
              placeholder="Project name, Barbour ID, postcode, or any keyword"
              className={`flex-1 ${INPUT_CLS}`}
              data-testid="crm-project-search"
            />
            <Button type="button" onClick={() => runProjectSearch(projectQuery)} disabled={projectLoading}>
              {projectLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>
          <div className="border-2 border-gray-200 rounded-xl bg-white divide-y max-h-64 overflow-y-auto">
            {projectLoading ? (
              <div className="p-4 text-center text-gray-500 text-sm">Searching projects…</div>
            ) : projects.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                {projectQuery.trim().length < 2 ? 'Type at least 2 characters to search' : 'No projects found'}
              </div>
            ) : (
              projects.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSelectedProject(p)}
                  className={`w-full text-left p-3 hover:bg-gray-50 flex items-center justify-between gap-2 transition-colors ${
                    selectedProject?.id === p.id ? 'bg-emerald-50 border-l-4 border-emerald-600' : ''
                  }`}
                  data-testid={`crm-project-option-${p.id}`}
                >
                  {/* Left: project name + secondary meta (stage, postcode etc.) */}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-gray-900 truncate">{p.deal_name}</div>
                    <div className="text-xs text-gray-500">
                      {[p.postcode, p.stage].filter(Boolean).join(' · ')}
                    </div>
                  </div>

                  {/* Right: Barbour ID badge OR Add Barbour ID button */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {p.barbour_project_id ? (
                      <span
                        className="flex items-center gap-1 text-[11px] font-bold text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full"
                        title="Barbour Project ID"
                      >
                        <Tag className="w-2.5 h-2.5" />
                        {p.barbour_project_id}
                      </span>
                    ) : (
                      <BarbourIdAdder project={p} onSaved={handleBarbourSaved} />
                    )}
                    {selectedProject?.id === p.id && <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
                  </div>
                </button>
              ))
            )}
          </div>
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setShowProjectCreate(true); setNewProjectName(projectQuery || quoteForm?.notes || ''); }}
              data-testid="crm-project-create-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Create new project
            </Button>
            {selectedProject && (
              <div className="text-xs text-emerald-700 font-semibold">✓ Project selected: {selectedProject.deal_name}</div>
            )}
          </div>

          {/* ───── Search Barbour — find a Barbour ABI project by postcode,
              then "Check CRM" per result (Barbour ID → postcode → name).
              Found → select it directly. Not found → jump into "Create new
              project" pre-filled with the Barbour result. ───── */}
          <div className="pt-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowBarbourSearch((v) => !v)}
              className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
              data-testid="crm-search-barbour-btn"
            >
              <Building2 className="w-4 h-4 mr-1" /> Search Barbour
            </Button>

            {showBarbourSearch && (
              <div className="mt-3 border-2 border-cyan-200 rounded-xl p-4 bg-cyan-50/30 space-y-3" data-testid="crm-search-barbour-panel">
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Label className="text-xs">Postcode</Label>
                    <Input
                      value={barbourPostcode}
                      onChange={(e) => setBarbourPostcode(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') searchBarbourForProject(); }}
                      placeholder="e.g. SW1A 1AA"
                      className={INPUT_CLS}
                      data-testid="crm-search-barbour-postcode"
                    />
                  </div>
                  <div className="w-28">
                    <Label className="text-xs">Radius (miles)</Label>
                    <Input
                      type="number"
                      min="0.05"
                      max={barbourMaxRadius}
                      step="0.05"
                      value={barbourRadius}
                      onChange={(e) => setBarbourRadius(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') searchBarbourForProject(); }}
                      className={INPUT_CLS}
                      data-testid="crm-search-barbour-radius"
                    />
                  </div>
                  <Button
                    type="button"
                    onClick={searchBarbourForProject}
                    disabled={barbourSearching || !barbourPostcode.trim()}
                    className="bg-cyan-600 hover:bg-cyan-700 text-white"
                    data-testid="crm-search-barbour-submit"
                  >
                    {barbourSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-[11px] text-gray-400">Max radius: {barbourMaxRadius} mile{barbourMaxRadius === 1 ? '' : 's'} (set in Settings &gt; Integrations &gt; Barbour)</p>

                {barbourSearchError && (
                  <p className="text-sm text-red-600" data-testid="crm-search-barbour-error">{barbourSearchError}</p>
                )}

                {barbourResults && (
                  <div className="divide-y divide-cyan-100 border border-cyan-200 rounded-lg bg-white max-h-72 overflow-y-auto" data-testid="crm-search-barbour-results">
                    {barbourResults.length === 0 ? (
                      <div className="p-4 text-center text-gray-500 text-sm">No Barbour projects found in that radius.</div>
                    ) : (
                      barbourResults.map((r) => {
                        const key = r.barbour_id || r.project_name;
                        const status = crmCheckStatus[key];
                        return (
                          <div key={key} className="p-3 flex items-center justify-between gap-3" data-testid={`crm-barbour-result-${key}`}>
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-sm text-gray-900 truncate">{r.project_name || '(unnamed project)'}</div>
                              <div className="text-xs text-gray-500 flex items-center gap-1 truncate">
                                <MapPin className="w-3 h-3 shrink-0" />
                                {[r.address, r.postcode].filter(Boolean).join(', ')}
                                {r.barbour_id && <span className="ml-1 font-mono text-[10px] text-cyan-700">#{r.barbour_id}</span>}
                              </div>
                            </div>
                            <div className="flex-shrink-0">
                              {!status && (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => checkCrmForBarbourResult(r)}
                                  data-testid={`crm-check-btn-${key}`}
                                >
                                  <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Check CRM
                                </Button>
                              )}
                              {status?.checking && (
                                <span className="text-xs text-gray-500 flex items-center gap-1">
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking…
                                </span>
                              )}
                              {status && !status.checking && status.found && (
                                <Button
                                  type="button"
                                  size="sm"
                                  onClick={() => selectCrmMatchFromBarbour(status.found)}
                                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                  data-testid={`crm-found-select-btn-${key}`}
                                >
                                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> In CRM — Select
                                </Button>
                              )}
                              {status && !status.checking && !status.found && (
                                <Button
                                  type="button"
                                  size="sm"
                                  onClick={() => addBarbourResultToCrm(r)}
                                  className="bg-blue-600 hover:bg-blue-700 text-white"
                                  data-testid={`crm-add-btn-${key}`}
                                >
                                  <Plus className="w-3.5 h-3.5 mr-1" /> Add to CRM
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {showProjectCreate && (
        <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50/30 space-y-3">
          <div className="font-semibold text-sm text-blue-900">New Project</div>
          <div>
            <Label className="text-xs">Project Name *</Label>
            <Input value={newProjectName} onChange={(e) => setNewProjectName(e.target.value)} className={INPUT_CLS} data-testid="crm-new-project-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Barbour ID (optional)</Label>
              <Input value={newProjectBarbourId} onChange={(e) => setNewProjectBarbourId(e.target.value)} className={INPUT_CLS} data-testid="crm-new-project-barbour" />
            </div>
            <div>
              <Label className="text-xs">Postcode (optional)</Label>
              <Input value={newProjectPostcode} onChange={(e) => setNewProjectPostcode(e.target.value)} className={INPUT_CLS} data-testid="crm-new-project-postcode" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowProjectCreate(false)}>Cancel</Button>
            <Button size="sm" onClick={createProject} disabled={creatingProject} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="crm-new-project-submit">
              {creatingProject ? (<><Loader2 className="w-4 h-4 animate-spin mr-1" />Creating…</>) : 'Create Project'}
            </Button>
          </div>
        </div>
      )}

      <div className="flex justify-between gap-2 pt-2">
        <Button variant="outline" onClick={() => setStep(0)}>← Back</Button>
        <Button
          onClick={() => setStep(2)}
          disabled={!selectedProject}
          className="bg-blue-600 hover:bg-blue-700 text-white"
          data-testid="crm-next-project-btn"
        >
          Next: Contractor &amp; Package
        </Button>
      </div>
    </>
  );
};
